/**
 * Guided tours, in very simple English: one per sidebar page, one per Business setup tab, one for the person
 * side panel and a short walk after the welcome. Every step points at ONE element marked in the page's own code
 * with `data-tour="<target>"` (no CSS guesses). A step whose element is not on the screen right now (an empty
 * list, a phone-sized menu) is left out, so a tour never points at nothing. A step with no target is shown in
 * the middle of the screen. `tours.test.tsx` renders every page and checks each target really exists.
 */
import { PAGE_TOUR_KEYS, type TourKey } from '@bs/contracts';

export interface TourStep { target?: string; title: string; text: string }
export interface Tour {
  key: TourKey;
  title: string;
  /** Page the tour runs on (path, plus `?tab=` for a setup tab). */
  path: string;
  search?: string;
  summary: string;
  steps: TourStep[];
  /** Selector clicked first when the tour needs something opened (the person panel opens the first person). */
  opener?: string;
}

const help: TourStep = { target: 'help-menu', title: 'Help is always here', text: 'Tap a small ? next to any title for a plain explanation. This Help button has all the tours.' };

const PAGES: Tour[] = [
  {
    key: 'today', title: 'Today', path: '/today', summary: 'Who to contact today and what to say.',
    steps: [
      { title: 'This is your Today page', text: 'Open it every morning. It shows who needs you today, in the best order. Dates are India time.' },
      { target: 'today-kpis', title: 'Your numbers', text: 'How many people need you, replies waiting, and promises due. Tap a box to jump to it.' },
      { target: 'today-list', title: 'Your list for today', text: 'Start at the top. Each row is one person and the next step. Example: "Ravi, send the price list".' },
      { target: 'today-promises', title: 'Promises', text: 'Things you or they said would happen. Example: "I will send the price list on Friday."' },
      { target: 'today-primary', title: 'Think through today', text: 'Ask the AI to plan your day. If it cannot help, you can still do it by hand.' },
      help,
    ],
  },
  {
    key: 'calendar', title: 'Calendar', path: '/calendar', summary: 'Your follow-ups and promises by date.',
    steps: [
      { title: 'This is your Calendar', text: 'Every next contact date, promise, meeting and festival, laid out by day. India time.' },
      { target: 'calendar-views', title: 'Month, week or day', text: 'Choose how much you want to see at once.' },
      { target: 'calendar-arrows', title: 'Move in time', text: 'Go back or forward one month, week or day.' },
      { target: 'calendar-today', title: 'Back to today', text: 'Jump back to today at any time.' },
      { target: 'calendar-grid', title: 'Your days', text: 'Each day shows who to contact and what is due. Tap an item to open the person.' },
      help,
    ],
  },
  {
    key: 'pipeline', title: 'Pipeline', path: '/pipeline', summary: 'Where every person and deal stands.',
    steps: [
      { title: 'This is your Pipeline', text: 'Every person you sell to, and how far along they are. Only you see your people.' },
      { target: 'pipeline-kpis', title: 'Quick numbers', text: 'How many people, whose turn it is, who is hot, and open deal value.' },
      { target: 'pipeline-primary', title: 'Add a person', text: 'Add someone new here. We check first so you do not add the same person twice.' },
      { target: 'pipeline-stages', title: 'Stages', text: 'How far each person has come. Tap a stage to see only those people. Example: "Proposal sent".' },
      { target: 'pipeline-views', title: 'Table or board', text: 'See people as a list, or as columns by stage.' },
      { target: 'pipeline-list', title: 'Your people', text: 'Tap a person to see their messages, promises, files and history.' },
      help,
    ],
  },
  {
    key: 'capture', title: 'Capture', path: '/capture', summary: 'Add people and conversations quickly.',
    steps: [
      { title: 'This is Capture', text: 'Paste a chat, a visiting card or a list. Business Sense pulls out the people and what was said.' },
      { target: 'capture-kpis', title: 'What is waiting', text: 'New leads and things still to do.' },
      { target: 'capture-tabs', title: 'Ways to capture', text: 'Pick how you want to add: a chat, call notes, leads, a card and more.' },
      { target: 'capture-panel', title: 'Paste here', text: 'Paste the WhatsApp chat or your notes. You check everything before it is saved.' },
      { target: 'capture-primary', title: 'Start with a chat', text: 'This button always brings you back to pasting a conversation.' },
      help,
    ],
  },
  {
    key: 'draft', title: 'Draft', path: '/draft', summary: 'Write a message, copy it, send it yourself.',
    steps: [
      { title: 'This is Draft', text: 'Write a message for one person. Business Sense checks it. You send it yourself.' },
      { target: 'draft-kpis', title: 'Your drafts', text: 'Waiting to send, sent, and thrown away.' },
      { target: 'draft-tabs', title: 'Write or look back', text: 'Write a new message, or see the ones you already made.' },
      { target: 'draft-composer', title: 'Write here', text: 'Pick the person and what you want. Then press Copy, send it from your phone, and press "I sent it".' },
      { target: 'draft-primary', title: 'New message', text: 'Start a fresh message at any time.' },
      help,
    ],
  },
  {
    key: 'reach', title: 'Reach', path: '/reach', summary: 'Your people list and outreach.',
    steps: [
      { title: 'This is Reach', text: 'Your full list of people, your WhatsApp lists and your LinkedIn contacts.' },
      { target: 'reach-kpis', title: 'Your list in numbers', text: 'How many people, how many you can message, and who needs a label.' },
      { target: 'reach-primary', title: 'Add a person', text: 'Add one person by hand.' },
      { target: 'reach-tabs', title: 'Tabs', text: 'People, files, lists, target companies and LinkedIn live here.' },
      { target: 'reach-views', title: 'Which people', text: 'Your Reach list, or the people already in your Pipeline.' },
      { target: 'reach-list', title: 'Your people', text: 'Search, sort and tap a person to see everything about them.' },
      help,
    ],
  },
  {
    key: 'media', title: 'Media', path: '/media', summary: 'Editors and journalists you build trust with.',
    steps: [
      { title: 'This is Media', text: 'Editors and journalists. They never get a sales message. Example: a health editor at a newspaper.' },
      { target: 'media-kpis', title: 'Your media numbers', text: 'Stories published this quarter, stories waiting, and who is due today.' },
      { target: 'media-primary', title: 'Add an editor', text: 'Add an editor or journalist you want to know better.' },
      { target: 'media-views', title: 'By stage or table', text: 'See editors as columns by stage, or as one list.' },
      { target: 'media-list', title: 'Your editors', text: 'Tap an editor to see their stories, notes and what to do next.' },
      help,
    ],
  },
  {
    key: 'strategy', title: 'Strategy', path: '/strategy', summary: 'Your target and the plan to reach it.',
    steps: [
      { title: 'This is Strategy', text: 'Set a money target and a date. Business Sense works out a plan from your real numbers.' },
      { target: 'strategy-kpis', title: 'Target and gap', text: 'Your target, what is already paid, what looks realistic, and the gap left.' },
      { target: 'strategy-primary', title: 'Make the plan', text: 'Set the target, then press to make or refresh the plan. The app does the sums, not the AI.' },
      { target: 'strategy-tabs', title: 'Plan parts', text: 'Plan, target, weekly review and more each have their own tab.' },
      { target: 'strategy-whatif', title: 'What if', text: 'Move the numbers and see the plan change. Example: "What if 2 more clients pay?"' },
      help,
    ],
  },
  {
    key: 'products', title: 'Products', path: '/products', summary: 'What you sell and how to explain it.',
    steps: [
      { title: 'This is Products', text: 'Everything you sell in this business. Every draft and suggestion reads these.' },
      { target: 'products-kpis', title: 'Products at a glance', text: 'Products in use, teaching notes done, updates and brochure files.' },
      { target: 'products-primary', title: 'Add a product', text: 'Add something you sell. Example: "Doctor outreach, yearly plan".' },
      { target: 'products-tabs', title: 'Three parts', text: 'The list, the teaching notes for each product, and your company background.' },
      { target: 'products-list', title: 'Your products', text: 'Tap a product to see its price, notes and files.' },
      help,
    ],
  },
  {
    key: 'laws', title: 'Selling rules', path: '/laws', summary: 'The rules that keep outreach polite and working.',
    steps: [
      { title: 'These are your selling rules', text: 'Simple rules every suggestion and draft follows. Example: "Do not chase twice without a reply."' },
      { target: 'laws-kpis', title: 'Rules in numbers', text: 'How many rules there are, how many are on here, and where you added notes.' },
      { target: 'laws-tabs', title: 'Look at them your way', text: 'All rules, rules by group, or facts about your business.' },
      { target: 'laws-list', title: 'Every rule', text: 'Tap a rule to read why it exists, add a note, or switch it off for this business.' },
      { target: 'laws-primary', title: 'Download', text: 'Save all rules as a file to read or share with your team.' },
      help,
    ],
  },
  {
    key: 'reports', title: 'Reports', path: '/reports', summary: 'How things are going.',
    steps: [
      { title: 'This is Reports', text: 'See how your sales are going, and get ready for meetings.' },
      { target: 'reports-title', title: 'Which business', text: 'Reports follow the business chosen at the top. Pick "All" to see everything.' },
      { target: 'reports-tabs', title: 'Kinds of report', text: 'Overview, week review, focus, lessons, wins and losses, and meeting prep.' },
      { target: 'reports-panel', title: 'The numbers', text: 'Counted from your own records. Move over a bar to see the exact number.' },
      { target: 'reports-primary', title: 'Week in review', text: 'A short look back at your week, in plain words.' },
      help,
    ],
  },
  {
    key: 'review', title: 'Review', path: '/review', summary: 'Your monthly review with advisors.',
    steps: [
      { title: 'This is Review', text: 'Your monthly review. Advisors answer only from your own data and never guess.' },
      { target: 'review-kpis', title: 'Where you stand', text: 'Your plan, this month\'s record and the facts filled in.' },
      { target: 'review-primary', title: 'Ask an advisor', text: 'Ask a question. Example: "Which clients should I focus on this month?"' },
      { target: 'review-facts', title: 'Facts only you know', text: 'Fill in things the app cannot see, like your team size.' },
      { target: 'review-record', title: 'Review record', text: 'Every answer and note is kept here, newest first.' },
      help,
    ],
  },
  {
    key: 'setup', title: 'Business setup', path: '/setup', summary: 'How Business Sense works for each business.',
    steps: [
      { title: 'This is Business setup', text: 'Everything that shapes how the app works for one business. Every change is saved with who and when.' },
      { target: 'setup-area', title: 'Pick a business', text: 'Each business area has its own setup. Example: Pharma and Education are set up apart.' },
      { target: 'setup-kpis', title: 'Setup at a glance', text: 'Knowledge notes, AI instructions, your own rules and the last change.' },
      { target: 'setup-tabs', title: 'Setup sections', text: 'Each tab has its own short tour the first time you open it.' },
      { target: 'setup-history', title: 'Undo any change', text: 'History shows every change. Press Restore to bring an older version back.' },
      help,
    ],
  },
  {
    key: 'tools', title: 'Tools', path: '/tools', summary: 'Downloads, backups, privacy and clean-up.',
    steps: [
      { title: 'This is Tools', text: 'Download your data, check backups, handle privacy requests and clean up duplicates.' },
      { target: 'tools-primary', title: 'Download everything', text: 'Get all your people in one spreadsheet.' },
      { target: 'tools-tabs', title: 'Kinds of tools', text: 'Data, privacy, duplicates, a LinkedIn post and a weekly update.' },
      { target: 'tools-panel', title: 'Your data', text: 'Downloads and a count of what you have stored. Example: 120 people, 40 files.' },
      help,
    ],
  },
  {
    key: 'users', title: 'Users', path: '/users', summary: 'Colleagues and the businesses they work in.',
    steps: [
      { title: 'This is Users', text: 'Your colleagues who can sign in, and which businesses each one works in.' },
      { target: 'users-kpis', title: 'Who is in', text: 'Everyone, who is active, and invites not yet accepted.' },
      { target: 'users-primary', title: 'Invite a colleague', text: 'Send an invite link. They choose their own password.' },
      { target: 'users-list', title: 'Your team', text: 'Tap a person to change which businesses they work in. Each person\'s clients stay private to them.' },
      help,
    ],
  },
  {
    key: 'settings', title: 'Settings', path: '/settings', summary: 'Your profile, businesses and display.',
    steps: [
      { title: 'This is Settings', text: 'Your own profile, the businesses you work in, and how the screen looks.' },
      { target: 'settings-profile', title: 'Your profile', text: 'Your name and email. Your clients and files are private to you.' },
      { target: 'settings-areas', title: 'Your businesses', text: 'Which business areas you work in. Example: Pharma and Education.' },
      { target: 'settings-display', title: 'Display', text: 'Switch between light and dark screens.' },
      { target: 'settings-primary', title: 'Manage users', text: 'Invite colleagues and choose their businesses.' },
      help,
    ],
  },
];

const tab = (id: string, title: string, summary: string, steps: TourStep[]): Tour => ({
  key: `setup-${id}` as TourKey, title: `Business setup: ${title}`, path: '/setup', search: id === 'profile' ? '?tab=profile' : `?tab=${id}`, summary,
  steps: [{ target: `setup-tab-${id}`, title, text: summary }, ...steps],
});

const SETUP_TABS: Tour[] = [
  tab('profile', 'Business profile', 'Who you are and what you sell. The AI reads this before it writes anything.', [
    { target: 'setup-profile-who', title: 'Who you are', text: 'Business name, your name and signature. Example: "Pranav, ImpactPlus".' },
    { target: 'setup-profile-sell', title: 'What you sell', text: 'What you sell and who buys it, in your own words.' },
    { target: 'setup-profile-voice', title: 'How it should sound', text: 'Your tone, and words or promises the AI must never use.' },
    { target: 'setup-profile-template', title: 'Starting template', text: 'Start from a ready-made profile and then adjust it.' },
    { target: 'setup-history', title: 'Undo', text: 'Every save shows here. Press Restore to go back.' },
  ]),
  tab('knowledge', 'Knowledge', 'Notes the AI reads for this business: facts, stories, answers to common questions.', [
    { target: 'setup-knowledge-add', title: 'Add knowledge', text: 'Add a note or attach a document. Example: "Our fees for 2026".' },
    { target: 'setup-knowledge-kinds', title: 'Kinds of notes', text: 'Show only one kind of note at a time.' },
    { target: 'setup-knowledge-list', title: 'Your notes', text: 'Tap a note to change it or switch it off. Only notes that are on are used.' },
  ]),
  tab('rules', 'Selling rules', 'The rules for this business. Reword one, add your own, or switch one off.', [
    { target: 'setup-rules-add', title: 'Add a rule', text: 'Write a rule of your own. Example: "Never call a doctor before 11 am."' },
    { target: 'setup-rules-filter', title: 'Filter', text: 'See all rules, only yours, reworded ones, or those switched off.' },
    { target: 'setup-rules-list', title: 'The rules', text: 'Tap a rule to change its words here. Some rules ask for a reason before a change.' },
    { target: 'setup-rules-notes', title: 'Notes and facts', text: 'Your notes on each rule live on the Selling rules page.' },
  ]),
  tab('ai', 'AI instructions', 'Give any AI feature extra instructions for this business.', [
    { target: 'setup-ai-intro', title: 'Try first', text: 'Your instructions go live only after you try them and say so.' },
    { target: 'setup-ai-group', title: 'Grouped by page', text: 'AI features are grouped by where you see them.' },
    { target: 'setup-ai-card', title: 'One AI feature', text: 'Tap to add words. Example: "Always mention our free first visit."' },
  ]),
  tab('lists', 'Lists', 'Names the app uses for this business: stages, contact types and key dates.', [
    { target: 'setup-lists-stages', title: 'Stage names', text: 'Rename a stage in your own words. Its meaning stays the same.' },
    { target: 'setup-lists-types', title: 'Contact types', text: 'One line on what each type of person means. Example: "Dean: decides on training."' },
    { target: 'setup-lists-calendar', title: 'Key dates', text: 'Festivals and dates the AI times greetings around. India dates.' },
    { target: 'setup-history', title: 'Undo', text: 'Every save shows here. Press Restore to go back.' },
  ]),
  tab('timing', 'Timing and numbers', 'How many days before someone is due again, message limits and reminder times.', [
    { target: 'setup-timing-stale', title: 'Days between contacts', text: 'Days without contact before a person shows as due. Example: 14 days.' },
    { target: 'setup-timing-limits', title: 'Message limits', text: 'How many messages in a row before you wait for a reply.' },
    { target: 'setup-timing-reminders', title: 'Morning brief', text: 'When your morning brief and reminders arrive. India time.' },
    { target: 'setup-timing-preview', title: 'What changes today', text: 'See who would move on Today before you save.' },
  ]),
  tab('transfer', 'Import and export', 'Copy a whole setup to a file, or load one in.', [
    { target: 'setup-transfer-export', title: 'Export', text: 'Download this business setup as one file. Good as a backup.' },
    { target: 'setup-transfer-import', title: 'Import', text: 'Load a setup file. The old setup stays in history, so you can go back.' },
    { title: 'Moving to a new business', text: 'Export from one business and import into another to start faster. Then change what differs.' },
  ]),
  tab('models', 'AI models', 'Which AI model does each job. The default is fine for most jobs.', [
    { target: 'setup-models-top', title: 'The default is fine', text: 'Change a model only if you have a reason. Every change is kept.' },
    { target: 'setup-models-scope', title: 'This business or all', text: 'Change the model for this business only, or for all of them.' },
    { target: 'setup-models-features', title: 'Each AI job', text: 'Press Change next to a job to pick another model and write why.' },
    { target: 'setup-models-catalog', title: 'All models', text: 'Every model, how fast it is and what it costs.' },
  ]),
];

const PERSON: Tour = {
  key: 'person', title: 'One person', path: '/pipeline', opener: '[data-tour="pipeline-list"] tbody tr', summary: 'Everything about one person in one panel.',
  steps: [
    { target: 'person-sheet', title: 'One person, one panel', text: 'Everything about this person: messages, promises, files and history.' },
    { target: 'person-badges', title: 'Where they stand', text: 'Stage, whose turn it is and the next contact date. Example: "Your turn to reply".' },
    { target: 'person-next', title: 'Next step', text: 'The one thing to do next with this person.' },
    { target: 'person-write', title: 'Write a message', text: 'Opens Draft with this person picked. You still send it yourself.' },
    { target: 'person-actions', title: 'More actions', text: 'Ask AI, prepare for a meeting, or stop suggesting this person.' },
    { target: 'person-tabs', title: 'Everything else', text: 'History, promises, messages, notes, AI, meeting prep and files.' },
  ],
};

const WELCOME_NEXT: Tour = {
  key: 'welcome-next', title: 'Your first day', path: '/today', summary: 'A short walk through the menu after the welcome.',
  steps: [
    { title: 'You are all set', text: 'Here is where your daily work lives. It takes one minute.' },
    { target: 'nav-today', title: 'Today', text: 'Start here every morning: who to contact and what to say.' },
    { target: 'nav-pipeline', title: 'Pipeline', text: 'Every person you sell to, and how far along they are.' },
    { target: 'nav-capture', title: 'Capture', text: 'Paste a WhatsApp chat or call notes. We pull out what matters.' },
    { target: 'nav-draft', title: 'Draft', text: 'Write a message, copy it, and send it from your own phone.' },
    { target: 'nav-calendar', title: 'Calendar', text: 'Every follow-up and promise by date.' },
    { target: 'help-menu', title: 'Tours for every page', text: 'Each page offers its own short tour the first time. Find them all here.' },
  ],
};

/** Every page tour also shows where the page sits in the menu (second step). */
export const TOURS: Tour[] = [
  ...PAGES.map((t) => ({
    ...t,
    steps: [t.steps[0]!, { target: `nav-${t.key}`, title: 'Find it in the menu', text: `${t.title} is always here in the menu on the left.` }, ...t.steps.slice(1)],
  })),
  ...SETUP_TABS,
  PERSON,
  WELCOME_NEXT,
];

export const tourFor = (key: TourKey): Tour => {
  const t = TOURS.find((x) => x.key === key);
  if (!t) throw new Error(`No tour for ${key}`);
  return t;
};

const PAGE_KEYS = new Set<string>(PAGE_TOUR_KEYS);

/**
 * Which tour belongs to where the user is: `/pipeline/<id>` is the person panel, `/setup?tab=x` the setup tab,
 * otherwise the page's own tour (by first path segment), or null.
 */
export function tourKeyForPath(pathname: string, search = ''): TourKey | null {
  const [, first = '', second] = pathname.split('/');
  if (first === 'pipeline' && second) return 'person';
  if (first === 'setup') {
    const t = new URLSearchParams(search).get('tab');
    if (t && TOURS.some((x) => x.key === `setup-${t}`)) return `setup-${t}` as TourKey;
  }
  return PAGE_KEYS.has(first) ? (first as TourKey) : null;
}

/** The element marked `data-tour="<target>"`, or null. Only a visible one counts in a real browser. */
export function findTarget(target: string | undefined, root: ParentNode = document): Element | null {
  if (!target) return null;
  const all = [...root.querySelectorAll(`[data-tour="${target}"]`)];
  // jsdom has no layout (every box is 0 x 0); in a browser skip hidden copies, e.g. the closed phone menu.
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)) return all[0] ?? null;
  return all.find((el) => el.getClientRects().length > 0) ?? null;
}
